<template>
  <div>
    <file-viewer v-model="fileModal" :files="files" :title="$t('files')" />
    <edit-expense v-model="edit" :preExpense="preExpense" @save="onSave" />

    <confirmation-modal v-model="approveModal" :item="item" @accept="approveExpense" :title="$t('confirm')"
      :message="$t('confirm_approve')" />
    <confirmation-modal v-model="deleteModal" :item="item" @accept="deleteExpense" :title="$t('confirm')"
      :message="$t('confirm_delete')" />

    <base-loading :visible="isLoading" />
    <div class="grid grid-cols-12 gap-4">
      <div class="col-span-full md:col-span-2 md:col-start-11  xs:col-start-1">
        <search-input v-model="search" @search="onSearch" @clear="onSearch" />
      </div>
      <div class="col-span-full col-start-1">
        <expense-table :items="items" @loadMore="loadMore" @sortChanged="onSortChanged" @approve="onApprove"
          @delete="onDelete" @edit="onEdit" @file="onFile" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useExpenseContainers } from '../../composables/useExpenseContainers.js'
import { useNotifier } from '@/composables/useNotifier.js'
import ExpenseTable from './components/ExpenseTable.vue'
import EditExpense from './components/EditExpense.vue'
import BaseLoading from '@/views/app/components/BaseLoading.vue'
import ConfirmationModal from '@/views/app/components/ConfirmationModal.vue'
import FileViewer from '@/views/app/components/FileViewer.vue'
import SearchInput from '@/views/app/components/SearchInput.vue'

const { notify } = useNotifier()

const item = ref({})
const preExpense = ref({})
const files = ref([])
const deleteModal = ref(false)
const approveModal = ref(false)
const fileModal = ref(false)
const edit = ref(false)
const expenses = useExpenseContainers()
const items = computed(() => expenses.state.items)
const currentPage = computed(() => expenses.state.currentPage)
const lastPage = computed(() => expenses.state.lastPage)
const isLoading = computed(() => expenses.state.loading)
const search = ref('')
const sort = ref({})

const loadData = async (params = {}) => {
  const payload = {
    page: expenses.state.currentPage || 1,
    search: search.value,
    column: sort.value.column,
    order: sort.value.order,
    ...params
  }
  const result = await expenses.getExpenses(payload)
  if (!result.success) notify({ title: 'error', text: 'message_error', color: 'danger' })
}

const loadMore = async () => {
  if (currentPage.value >= lastPage.value) return
  const newPage = expenses.state.currentPage + 1
  await loadData({ page: newPage })
}

const onSearch = async () => {
  await loadData({ page: 1, search: search.value })
}

const onSortChanged = async (sortData) => {
  sort.value.column = sortData.key
  sort.value.order = sortData.order
  await loadData({ column: sort.value.column, order: sort.value.order })
}

const onApprove = (row) => {
  approveModal.value = true
  item.value = { ...row }
}

const approveExpense = async () => {
  const payload = { id: item.value.id }
  const result = await expenses.approveExpense(payload)
  approveModal.value = false
  if (!result.success) {
    notify({ title: 'attention', text: 'message_error', color: 'warning' })
    return
  }
  const expenseIndex = expenses.state.items.findIndex(el => el.id === payload.id)
  if (expenseIndex !== -1) {
    expenses.state.items[expenseIndex].approved = '1'
  }
  notify({ title: 'success', text: 'message_update', color: 'success' })
}

const onDelete = async (row) => {
  deleteModal.value = true
  item.value = { ...row }
}

const deleteExpense = async () => {
  const payload = { id: item.value.id }
  const result = await expenses.deleteExpense(payload)
  deleteModal.value = false
  if (!result.success) {
    notify({ title: 'attention', text: 'message_error', color: 'warning' })
    return
  }

  const expenseIndex = expenses.state.items.findIndex(el => el.id === payload.id)
  if (expenseIndex !== -1) {
    expenses.state.items.splice(expenseIndex, 1)
  }

  notify({ title: 'success', text: 'message_delete', color: 'success' })
}

const onFile = async (row) => {
  const payload = { id: row.id }
  const result = await expenses.getFiles(payload)
  if (!result.success) {
    notify({ title: 'attention', text: 'message_error', color: 'warning' })
    return
  }
  if (result.success && result.response && Array.isArray(result.response.file)) {
    files.value = [...result.response.file];
    fileModal.value = true;
  } else {
    files.value = [];
  }
}

const onEdit = async (row) => {
  const payload = { id: row.id }
  const result = await expenses.getExpense(payload)
  if (!result.success) {
    notify({ title: 'attention', text: 'message_error', color: 'warning' })
    return
  }
  preExpense.value = {
    containers: result.response.containers.map((el) => ({ ...el, active: el.deleted_at == null ? true : false })),
    entry: { ...result.response.pre_expense }
  }
  edit.value = true
}

const onSave = async (payload) => {
  const result = await expenses.updateExpense(payload)
  if (!result.success) {
    notify({ title: 'attention', text: 'message_error', color: 'warning' })
    return
  }

  const expenseIndex = expenses.state.items.findIndex(r => r.id === payload.id)
  if (expenseIndex !== -1) {
    expenses.state.items[expenseIndex].total = payload.total
  }
  edit.value = false
  notify({ title: 'success', text: 'message_update', color: 'success' })
}


onMounted(() => loadData())

</script>
